/**
 * Delta Tool Wrapper
 *
 * Wraps mutating tool execute functions to capture delta snapshots
 * before each tool executes. The delta chain accumulates per-turn
 * and is used for efficient undo/diff operations.
 */

import { getAffectedRanges, isMutatingTool } from './affected-range'
import {
  markAllSheetsDirty,
  markDirtyFromAddresses,
  markSheetDirty,
} from './dirty-tracker'
import type { ToolDelta, TurnDeltaChain } from './snapshot-db'
import { saveDeltaChain } from './snapshot-db'

// ============================================================================
// Current Turn State
// ============================================================================

let currentChain: TurnDeltaChain | null = null
let captureFunction:
  | ((
      toolName: string,
      toolCallId: string,
      args: Record<string, unknown>
    ) => Promise<ToolDelta | null>)
  | null = null

/**
 * Initialize a new delta chain for a turn.
 * Must be called before tools start executing.
 */
export function startDeltaChain(
  turnId: string,
  chatId: string,
  captureFn: (
    toolName: string,
    toolCallId: string,
    args: Record<string, unknown>
  ) => Promise<ToolDelta | null>
): void {
  currentChain = {
    key: `${chatId}:${turnId}`,
    turnId,
    chatId,
    deltas: [],
    isComplete: true,
    timestamp: Date.now(),
  }
  captureFunction = captureFn
  console.log(`[DeltaWrapper] Started delta chain for turn ${turnId}`)
}

/**
 * Get the current delta chain (for inspection/finalization).
 */
export function getCurrentDeltaChain(): TurnDeltaChain | null {
  return currentChain
}

/**
 * Add a pre-captured delta to the current chain.
 * Called by the tool wrapper before the tool executes.
 */
function addDeltaToChain(delta: ToolDelta): void {
  if (!currentChain) return
  currentChain.deltas.push(delta)
}

/**
 * Mark the current chain as incomplete (a delta capture failed).
 * The system will fall back to full snapshot restore.
 */
function markChainIncomplete(): void {
  if (!currentChain) return
  currentChain.isComplete = false
  console.warn(
    '[DeltaWrapper] Delta chain marked incomplete — will use full snapshot fallback'
  )
}

/**
 * Finalize and save the current delta chain.
 * Called after all tools in a turn have executed.
 */
export async function finalizeDeltaChain(): Promise<TurnDeltaChain | null> {
  if (!currentChain) return null

  const chain = currentChain
  currentChain = null
  captureFunction = null

  // Only save if we captured at least one delta
  if (chain.deltas.length > 0) {
    await saveDeltaChain(chain)
  }

  console.log(
    `[DeltaWrapper] Finalized delta chain for turn ${chain.turnId}: ` +
      `${chain.deltas.length} deltas, complete=${chain.isComplete}`
  )

  return chain
}

/**
 * Discard the current delta chain without saving.
 */
export function discardDeltaChain(): void {
  if (currentChain) {
    console.log(
      `[DeltaWrapper] Discarded delta chain for turn ${currentChain.turnId}`
    )
  }
  currentChain = null
  captureFunction = null
}

// ============================================================================
// Dirty Tracking Integration
// ============================================================================

/**
 * Update dirty tracker based on tool call and args.
 */
function updateDirtyTracker(
  toolName: string,
  args: Record<string, unknown>
): void {
  const affected = getAffectedRanges(toolName, args)
  for (const sheetName of affected.dirtySheets) {
    if (sheetName === null) {
      // null means we can't determine the sheet — mark all dirty
      markAllSheetsDirty()
    } else {
      markSheetDirty(sheetName)
    }
  }

  // Also extract addresses from common arg patterns
  const addressFields = [
    'address',
    'sourceAddress',
    'targetAddress',
    'startCell',
  ]
  const addresses: string[] = []
  for (const field of addressFields) {
    if (typeof args[field] === 'string') {
      addresses.push(args[field] as string)
    }
  }
  // Handle addresses array (readCellRanges, setBorders, etc.)
  if (Array.isArray(args.addresses)) {
    for (const addr of args.addresses as string[]) {
      if (typeof addr === 'string') {
        addresses.push(addr)
      }
    }
  }
  // Handle items array (writeCellRanges, clearCellRange, etc.)
  if (Array.isArray(args.items)) {
    for (const item of args.items as Array<{ address?: string }>) {
      if (typeof item.address === 'string') {
        addresses.push(item.address)
      }
    }
  }
  if (addresses.length > 0) {
    markDirtyFromAddresses(addresses)
  }
}

// ============================================================================
// Tool Wrapping
// ============================================================================

type AnyToolDef = {
  execute?: (...args: unknown[]) => unknown
  [key: string]: unknown
}

/**
 * Wrap a set of tools with delta capture.
 * Only wraps mutating tools — read-only tools pass through unchanged.
 * Returns a new tools object with wrapped execute functions.
 */
export function wrapToolsWithDeltaCapture<T extends Record<string, AnyToolDef>>(
  tools: T
): T {
  const wrapped = { ...tools }

  for (const [toolName, toolDef] of Object.entries(tools)) {
    if (!toolDef.execute || !isMutatingTool(toolName)) {
      continue
    }

    const originalExecute = toolDef.execute
    wrapped[toolName as keyof T] = {
      ...toolDef,
      execute: async (...executeArgs: unknown[]) => {
        // The execute function receives parsed args as the first argument
        const args = (executeArgs[0] ?? {}) as Record<string, unknown>

        // Update dirty tracker
        updateDirtyTracker(toolName, args)

        // Capture delta before tool executes (if chain is active)
        if (currentChain && captureFunction) {
          try {
            const toolCallId = `${toolName}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
            const delta = await captureFunction(toolName, toolCallId, args)
            if (delta) {
              addDeltaToChain(delta)
            }
          } catch (error) {
            console.warn(
              `[DeltaWrapper] Failed to capture delta for ${toolName}`,
              error
            )
            markChainIncomplete()
          }
        }

        // Execute the original tool
        return originalExecute(...executeArgs)
      },
    } as T[keyof T]
  }

  return wrapped
}
